/* Phase 2 — Meta Embedded Signup setup page.
 *
 * Replaces the prior QR-scan wizard (whatsapp-web.js path, dropped due
 * to puppeteer issues). Plan: .claude/plans/whatsapp-feature.md, Phase 2.
 *
 * Flow:
 *   1. On mount, fetch existing connection from
 *      GET /whatsapp/v2/meta/embedded-signup/connection.
 *      If connected → "Connected" state with phone + WABA details.
 *   2. If not connected AND no `?code=` in URL → "Connect" CTA. Click
 *      opens Meta-hosted signup URL in same tab.
 *   3. Meta redirects back here with `?code=<oauth>`. On mount we
 *      detect the code and POST it to /exchange, then transition to
 *      Connected state.
 *
 * Visual recipe matches the rest of the merchant dashboard (clean
 * card surface, brand-color CTA, status pill). No spinners — show
 * skeleton text instead.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  AlertCircle,
  Shield,
  ExternalLink,
  RefreshCw,
  Bot,
} from 'lucide-react';
import WhatsAppIcon from '../sales-tools/WhatsAppIcon';
import axiosInstance from '../../axiosConfig';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import './WhatsAppSetup.css';

/* ------------------------------------------------------------------ */
/* Module-scope helpers                                               */
/* ------------------------------------------------------------------ */

// Defaults new merchants inherit for the assistant. Backend seeds the
// same values in CustomerAutomationSettings.DEFAULTS; keep in sync when
// adding fields.
const DEFAULT_AUTOMATION_SETTINGS = {
  aiAssistantEnabled: true,
  productAutoReplies: true,
  orderStatusReplies: true,
  orderUpdateNotifications: false,
  abandonedCartReminders: false,
  autoResumeMinutes: 10,
};
const AUTO_RESUME_MIN = 1;
const AUTO_RESUME_MAX = 120;

const STATUS = {
  LOADING: 'loading',
  NOT_CONNECTED: 'not_connected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatMetaValue(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

function getMetaPhoneReadiness(connection) {
  const metaPhone = connection?.settings?.metaPhone || {};
  const codeStatus = String(metaPhone.codeVerificationStatus || '').toUpperCase();
  const isCodeVerified = connection?.isVerified || codeStatus === 'VERIFIED';
  const hasWebhook = !!connection?.lastWebhookReceived;

  if (!isCodeVerified) {
    return {
      state: 'pending',
      label: 'Meta phone pending',
      message: 'Meta has not marked this phone number as ready yet. Customer messages may not arrive until Meta completes phone/display-name review.',
    };
  }

  if (!hasWebhook) {
    return {
      state: 'pending',
      label: 'Meta phone pending',
      message: 'The number is connected and code-verified in Revstar, but Meta Manager still needs to move it out of Pending before live customer messages can be relied on.',
    };
  }

  return {
    state: 'active',
    label: 'Ready',
    message: 'Meta verification and live webhook delivery have both been seen by Revstar.',
  };
}

/* ------------------------------------------------------------------ */
/* Subcomponents                                                      */
/* ------------------------------------------------------------------ */

function LoadingCard() {
  return (
    <div className="wa-setup-card wa-setup-card--neutral">
      <div className="wa-setup-skel" />
      <div className="wa-setup-skel wa-setup-skel--short" />
    </div>
  );
}

function ConnectCard({ signupUrl, onConnect }) {
  return (
    <div className="wa-setup-card wa-setup-card--connect">
      <div className="wa-setup-icon wa-setup-icon--brand">
        <WhatsAppIcon size={30} />
      </div>
      <h2 className="wa-setup-h2">Connect your WhatsApp Business Account</h2>
      <p className="wa-setup-body">
        Link your WhatsApp Business number to Revstar to receive customer messages
        in one inbox, share product cards, and convert chats into paid orders —
        without leaving the conversation.
      </p>
      <ul className="wa-setup-list">
        <li><Shield size={14} /> Secure OAuth — Meta handles the connection</li>
        <li><CheckCircle size={14} /> Works with your existing WhatsApp Business number</li>
        <li><CheckCircle size={14} /> One-click setup; no app to install</li>
      </ul>
      <button
        type="button"
        className="wa-setup-cta"
        onClick={onConnect}
        disabled={!signupUrl}
        title={!signupUrl ? 'Setup not yet configured — contact Revstar support' : ''}
      >
        Connect via Meta
        <ExternalLink size={14} />
      </button>
      {!signupUrl && (
        <p className="wa-setup-hint wa-setup-hint--warn">
          WhatsApp connection isn't available yet. Check back shortly.
        </p>
      )}
    </div>
  );
}

function ConnectingCard({ error }) {
  if (error) {
    return (
      <div className="wa-setup-card wa-setup-card--connect wa-setup-card--error">
        <div className="wa-setup-icon wa-setup-icon--warn">
          <AlertCircle size={28} />
        </div>
        <h2 className="wa-setup-h2">Connection failed</h2>
        <p className="wa-setup-body">{error}</p>
        <p className="wa-setup-hint">
          Try connecting again from the setup page. If the problem persists,
          contact Revstar support.
        </p>
      </div>
    );
  }
  return (
    <div className="wa-setup-card wa-setup-card--connect">
      <div className="wa-setup-icon wa-setup-icon--brand">
        <RefreshCw size={28} className="wa-setup-spin" />
      </div>
      <h2 className="wa-setup-h2">Finishing your connection…</h2>
      <p className="wa-setup-body">
        Exchanging the signup token with Meta and registering your WABA. This
        takes a few seconds.
      </p>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 *   Redesigned settings page (v3) — minimal, hierarchical.
 *
 *   Radical simplification per user feedback: v2 was still a wall of
 *   equal-weight rows. v3 leads with the ONE decision merchants come
 *   here for (AI on/off) as a hero. Everything else is either a
 *   compact group of tight rows (labels + controls, NO paragraphs) or
 *   collapsed into an "Advanced" details footer.
 *
 *   Layout:
 *     [status strip: ● Connected · +234... · GTHub Stores      ⋮]
 *     [HERO: AI assistant [big toggle] — one-line explanation]
 *     [Answers  — 4 compact toggles, no descriptions]
 *     [Handover — 1 compact row with number input]
 *     [Advanced ⌄] — expands: connection metadata + Disconnect
 *
 *   Catalog section removed entirely — admin territory per product
 *   decision; merchants don't self-serve catalog syncs anymore.
 * ═══════════════════════════════════════════════════════════════════ */

function ConnectedCard({ connection, onDisconnect, disconnecting, toast, onConnectionUpdated }) {
  return (
    <div className="waset3-page">
      <StatusStrip connection={connection} />
      <PinNudge connection={connection} onConnectionUpdated={onConnectionUpdated} />
      <CatalogSection toast={toast} />
      <AssistantSectionV3 connection={connection} toast={toast} />
      <AdvancedDetails connection={connection} onDisconnect={onDisconnect} disconnecting={disconnecting} />
    </div>
  );
}

/* ── Status strip (top): one subtle line, no card ─────────────── */

function StatusStrip({ connection }) {
  const readiness = getMetaPhoneReadiness(connection);
  const isActive = readiness.state === 'active';
  return (
    <div className={`waset3-status ${isActive ? 'waset3-status--ok' : 'waset3-status--warn'}`}>
      <span className="waset3-status__dot" aria-hidden="true" />
      <span className="waset3-status__label">
        {isActive ? 'Connected' : (readiness.label || 'Attention needed')}
      </span>
      <span className="waset3-status__sep">·</span>
      <span className="waset3-status__mono">{connection.phoneNumber || '—'}</span>
      {connection.displayName && (
        <>
          <span className="waset3-status__sep">·</span>
          <span className="waset3-status__name">{connection.displayName}</span>
        </>
      )}
    </div>
  );
}

/* Merge our friendly title with Meta's raw message. Meta's code 100
   is generic — the raw message usually says why. */
function composePinError(payload, fallback) {
  const title = payload?.userTitle;
  const raw = payload?.error;
  if (title && raw && !String(raw).toLowerCase().includes(String(title).toLowerCase()))
    return `${title}\n${raw}`;
  return title || raw || fallback || 'Could not set PIN.';
}

/* ── PIN nudge (shows only when the number was never registered
       with the Cloud API — mirrors mobile SettingsScreen PinNudge) ── */

function PinNudge({ connection, onConnectionUpdated }) {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const registered = Boolean(connection?.settings?.cloudApiRegistered);
  // Meta wouldn't move the number to "active" readiness (verified +
  // webhook received) without a PIN in place, regardless of whether
  // we or the merchant set it — so trust Meta's state too.
  const readiness = getMetaPhoneReadiness(connection);
  if (registered || readiness.state === 'active') return null;

  const submit = async () => {
    setError(null);
    if (!/^\d{6}$/.test(pin)) {
      setError('PIN must be exactly 6 digits.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axiosInstance.post(
        '/whatsapp/v2/meta/embedded-signup/register-pin',
        { pin }
      );
      if (data?.ok) {
        setOpen(false);
        setPin('');
        onConnectionUpdated?.();
      } else {
        setError(composePinError(data));
      }
    } catch (err) {
      setError(composePinError(err?.response?.data, err?.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="waset3-pin-nudge">
        <div className="waset3-pin-nudge__body">
          <strong>Set your registration PIN</strong>
          <span>Meta needs a 6-digit PIN before this number can send or receive on Revstar.</span>
        </div>
        <button
          type="button"
          className="waset3-pin-nudge__btn"
          onClick={() => { setPin(''); setError(null); setOpen(true); }}
        >
          Set PIN
        </button>
      </div>
      {open && (
        <div className="waset3-pin-modal" role="dialog" aria-modal="true">
          <div
            className="waset3-pin-modal__backdrop"
            onClick={() => { if (!submitting) setOpen(false); }}
          />
          <div className="waset3-pin-modal__card">
            <h3>Set registration PIN</h3>
            <p>
              Choose any 6-digit PIN for {connection.phoneNumber || 'this number'}. You&apos;ll
              need it if you ever move this number away from Revstar. Meta stores it — we don&apos;t.
            </p>
            <input
              className="waset3-pin-modal__input"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={pin}
              placeholder="123456"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pin.length === 6 && !submitting) submit();
                if (e.key === 'Escape' && !submitting) setOpen(false);
              }}
              autoFocus
            />
            {error && <div className="waset3-pin-modal__error">{error}</div>}
            <div className="waset3-pin-modal__actions">
              <button
                type="button"
                className="waset3-pin-modal__cancel"
                onClick={() => setOpen(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="waset3-pin-modal__submit"
                onClick={submit}
                disabled={submitting || pin.length !== 6}
              >
                {submitting ? 'Setting…' : 'Set PIN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Products on WhatsApp (catalog sync — expandable) ─────────
       Collapsed: "N of M synced · [state pill]" + chevron.
       Expanded : plan cap, last sync timestamp, Re-sync button.
       Renders nothing until /catalog/status resolves — the section
       is meaningless without a merchant tier + counts. */

function CatalogSection({ toast }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/whatsapp/v2/catalog/status');
      if (data?.ok) setStatus(data);
    } catch (err) {
      // 412 NOT_CONNECTED etc. — silently hide the section
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resync = async () => {
    setSyncing(true);
    try {
      const { data } = await axiosInstance.post('/whatsapp/v2/catalog/sync');
      if (data?.ok) {
        // `data.skipped` and `data.errors` are arrays of objects — must
        // use .length, otherwise the toast prints "[object Object],..."
        const skippedCount = Array.isArray(data.skipped) ? data.skipped.length : 0;
        const failedCount  = Array.isArray(data.errors)  ? data.errors.length  : 0;
        const summary = [
          `Synced ${data.synced ?? 0} items to WhatsApp`,
          skippedCount ? `${skippedCount} skipped` : null,
          failedCount  ? `${failedCount} failed`   : null,
        ].filter(Boolean).join(' · ');
        toast?.success?.(summary);
        await load();
      } else {
        toast?.error?.(data?.error || 'Could not sync products.');
      }
    } catch (err) {
      const payload = err?.response?.data;
      toast?.error?.(payload?.error?.message || payload?.error || err.message || 'Could not sync products.');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return null;
  if (!status) return null;

  const pillLabel =
    status.catalogStatus === 'READY'             ? 'Live'
  : status.catalogStatus === 'PROVISIONING'      ? 'Provisioning'
  : status.catalogStatus === 'LINK_PENDING'      ? 'Linking'
  : status.catalogStatus === 'FAILED'            ? 'Failed'
  : status.catalogStatus === 'ERROR'             ? 'Error'
  : status.catalogStatus === 'PERMISSION_DENIED' ? 'Permission pending'
  : status.catalogStatus === 'NOT_SELECTED'      ? 'No catalog chosen'
  : status.catalogStatus === 'NOT_PROVISIONED'   ? 'Not set up'
  :                                                status.catalogStatus;
  const pillMod =
    status.catalogStatus === 'READY'              ? 'is-ok'
  : status.catalogStatus === 'FAILED'             ? 'is-danger'
  : status.catalogStatus === 'ERROR'              ? 'is-danger'
  :                                                 'is-warn';

  const planLine = status.productLimit == null
    ? `Your ${status.merchantTier} plan syncs unlimited products`
    : `Your ${status.merchantTier} plan syncs up to ${status.productLimit} products`;

  return (
    <section className="waset3-catalog">
      <button
        type="button"
        className="waset3-catalog__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div className="waset3-catalog__summary">
          {/* catalogProductCount counts catalog *items* (variants explode
              into separate items), totalProducts counts merchant product
              rows. They're different units, so an "X of Y" ratio misleads.
              Show the item count that customers actually see on WhatsApp. */}
          <strong>{status.catalogProductCount ?? 0}</strong>
          <span className="waset3-catalog__sep">items on WhatsApp</span>
        </div>
        <span className={`waset3-catalog__pill ${pillMod}`}>{pillLabel}</span>
        <span className={`waset3-catalog__chev ${open ? 'is-open' : ''}`} aria-hidden="true">›</span>
      </button>
      {open && (
        <div className="waset3-catalog__body">
          {status.catalogStatus === 'PERMISSION_DENIED' ? (
            <div className="waset3-catalog__perm">
              <strong>Waiting on Meta approval</strong>
              <span>
                Revstar&apos;s Meta app doesn&apos;t yet have permission to create WhatsApp
                catalogs on your account. Once approved, disconnect and reconnect your
                WhatsApp number and sync will resume automatically.
              </span>
            </div>
          ) : status.catalogStatus === 'NOT_SELECTED' ? (
            <div className="waset3-catalog__perm">
              <strong>No catalog chosen yet</strong>
              <span>
                You didn&apos;t pick a WhatsApp catalog during signup. Disconnect and
                reconnect your WhatsApp number, then select or create a catalog when
                Meta&apos;s signup form asks.
              </span>
            </div>
          ) : (
            <>
              <dl className="waset3-details">
                <div><dt>Plan</dt><dd>{planLine}</dd></div>
                <div><dt>Last sync</dt><dd>{fmtDate(status.lastCatalogSync)}</dd></div>
                {status.catalogId && (
                  <div><dt>Catalog</dt><dd className="waset3-mono">{status.catalogId}</dd></div>
                )}
              </dl>
              <button
                type="button"
                className="waset3-catalog__resync"
                onClick={resync}
                disabled={syncing || !status.catalogId}
              >
                {syncing ? 'Working…' : 'Re-sync products'}
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}

/* ── Advanced details (bottom, collapsed) ─────────────────────── */

function AdvancedDetails({ connection, onDisconnect, disconnecting }) {
  const [open, setOpen] = useState(false);
  const metaPhone = connection.settings?.metaPhone || {};
  return (
    <div className="waset3-advanced">
      <button
        type="button"
        className="waset3-advanced__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>Advanced</span>
        <span className={`waset3-advanced__chev ${open ? 'is-open' : ''}`} aria-hidden="true">›</span>
      </button>
      {open && (
        <div className="waset3-advanced__body">
          <dl className="waset3-details">
            <div><dt>Phone</dt><dd className="waset3-mono">{connection.phoneNumber || '—'}</dd></div>
            <div><dt>Display name</dt><dd>{connection.displayName || '—'}</dd></div>
            <div><dt>Verified</dt><dd>{connection.isVerified ? 'Yes' : (formatMetaValue(metaPhone.codeVerificationStatus) || 'No')}</dd></div>
            <div><dt>Quality</dt><dd>{formatMetaValue(metaPhone.qualityRating)}</dd></div>
            <div><dt>Connected</dt><dd>{fmtDate(connection.createdAt)}</dd></div>
            <div><dt>Last webhook</dt><dd>{fmtDate(connection.lastWebhookReceived)}</dd></div>
            <div><dt>Last sent</dt><dd>{fmtDate(connection.lastMessageSent)}</dd></div>
          </dl>
          <button
            type="button"
            className="waset3-disconnect"
            onClick={onDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? 'Disconnecting…' : 'Disconnect WhatsApp'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Assistant section: hero + compact groups + sticky save bar ─ */

function AssistantSectionV3({ connection, toast }) {
  const [settings, setSettings] = useState({
    ...DEFAULT_AUTOMATION_SETTINGS,
    ...(connection.settings || {}),
  });
  const [saved, setSaved] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next = { ...DEFAULT_AUTOMATION_SETTINGS, ...(connection.settings || {}) };
    setSettings(next);
    setSaved(next);
  }, [connection.settings]);

  const dirty = (
    settings.aiAssistantEnabled !== saved.aiAssistantEnabled ||
    settings.productAutoReplies !== saved.productAutoReplies ||
    settings.orderStatusReplies !== saved.orderStatusReplies ||
    settings.orderUpdateNotifications !== saved.orderUpdateNotifications ||
    settings.abandonedCartReminders !== saved.abandonedCartReminders ||
    Number(settings.autoResumeMinutes) !== Number(saved.autoResumeMinutes)
  );

  const setField = (key, value) => setSettings((cur) => ({ ...cur, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put('/whatsapp/v2/settings', { settings });
      if (data?.ok) {
        const next = { ...DEFAULT_AUTOMATION_SETTINGS, ...(data.settings || {}) };
        setSettings(next);
        setSaved(next);
        toast.success('Automation controls saved.');
      } else {
        toast.error(data?.error || 'Could not save automation controls.');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const aiOn = !!settings.aiAssistantEnabled;

  const clampMinutes = (v) => Math.min(AUTO_RESUME_MAX, Math.max(AUTO_RESUME_MIN, v));

  return (
    <>
      {/* HERO: the master switch. This is the ONE decision merchants
          come to this page for. Big, obvious, one line of context. */}
      <section className="waset3-hero">
        <div className="waset3-hero__icon" aria-hidden="true">
          <Bot size={22} />
        </div>
        <div className="waset3-hero__copy">
          <h2 className="waset3-hero__title">AI assistant</h2>
          <p className="waset3-hero__sub">
            {aiOn
              ? 'Answering customer messages automatically. You can take over any conversation.'
              : 'Off — every reply on this number comes from you.'}
          </p>
        </div>
        <MasterSwitch checked={aiOn} onChange={(v) => setField('aiAssistantEnabled', v)} />
      </section>

      {/* GROUP 1: Answers — tight compact list, no descriptions.
          Dimmed as a whole when the master switch is off. */}
      <CompactGroup label="Answers" disabled={!aiOn}>
        <CompactRow
          title="Product questions"
          checked={aiOn && !!settings.productAutoReplies}
          disabled={!aiOn}
          onChange={(v) => setField('productAutoReplies', v)}
        />
        <CompactRow
          title="Order status"
          checked={aiOn && !!settings.orderStatusReplies}
          disabled={!aiOn}
          onChange={(v) => setField('orderStatusReplies', v)}
        />
        <CompactRow
          title="Order updates"
          badge="Template"
          checked={aiOn && !!settings.orderUpdateNotifications}
          disabled={!aiOn}
          onChange={(v) => setField('orderUpdateNotifications', v)}
        />
        <CompactRow
          title="Cart reminders"
          badge="Template"
          checked={aiOn && !!settings.abandonedCartReminders}
          disabled={!aiOn}
          onChange={(v) => setField('abandonedCartReminders', v)}
        />
      </CompactGroup>

      {/* GROUP 2: Handover behavior — one row. */}
      <CompactGroup label="Handover" disabled={!aiOn}>
        <CompactNumberRow
          title="Resume after I'm silent for"
          unit="min"
          min={AUTO_RESUME_MIN}
          max={AUTO_RESUME_MAX}
          value={Number(settings.autoResumeMinutes) || DEFAULT_AUTOMATION_SETTINGS.autoResumeMinutes}
          disabled={!aiOn}
          onChange={(v) => setField('autoResumeMinutes', clampMinutes(v))}
        />
      </CompactGroup>

      {/* Sticky save bar — appears only when dirty. Standard modern
          settings-page pattern (Notion, Linear, GitHub). */}
      {dirty && (
        <div className="waset3-savebar" role="status">
          <span className="waset3-savebar__msg">You have unsaved changes</span>
          <button
            type="button"
            className="waset3-savebar__discard"
            onClick={() => setSettings(saved)}
            disabled={saving}
          >
            Discard
          </button>
          <button
            type="button"
            className="waset3-savebar__save"
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </>
  );
}

/* ── Compact primitives (v3) ────────────────────────────── */

function MasterSwitch({ checked, onChange }) {
  return (
    <label className="waset3-master">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label="AI assistant"
      />
      <span className="waset3-master__track" aria-hidden="true" />
    </label>
  );
}

function CompactGroup({ label, disabled, children }) {
  return (
    <section className={`waset3-group ${disabled ? 'is-disabled' : ''}`}>
      <h3 className="waset3-group__label">{label}</h3>
      <div className="waset3-group__body">{children}</div>
    </section>
  );
}

function CompactRow({ title, badge, checked, disabled, onChange }) {
  return (
    <label className={`waset3-row ${disabled ? 'is-disabled' : ''}`}>
      <span className="waset3-row__title">
        {title}
        {badge && <span className="waset3-row__badge">{badge}</span>}
      </span>
      <span className="waset3-row__control">
        <input
          type="checkbox"
          className="waset3-switch__input"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          aria-label={title}
        />
        <span className="waset3-switch" aria-hidden="true" />
      </span>
    </label>
  );
}

function CompactNumberRow({ title, value, unit, min, max, disabled, onChange }) {
  return (
    <div className={`waset3-row ${disabled ? 'is-disabled' : ''}`}>
      <span className="waset3-row__title">{title}</span>
      <span className="waset3-row__control waset3-number">
        <input
          type="number"
          min={min}
          max={max}
          step={1}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const raw = parseInt(e.target.value, 10);
            if (Number.isFinite(raw)) onChange(raw);
          }}
          aria-label={title}
        />
        {unit && <span className="waset3-number__unit">{unit}</span>}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */

export default function WhatsAppSetup() {
  const location = useLocation();
  const navigate = useNavigate();

  const toast = useToast();
  const { confirm } = useConfirm();

  const [status, setStatus] = useState(STATUS.LOADING);
  const [connection, setConnection] = useState(null);
  const [signupUrl, setSignupUrl] = useState(null);
  const [error, setError] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);

  /* Pull both config and the merchant's current connection in parallel.
     If they have a connection we don't need the signupUrl, but loading
     it doesn't hurt and keeps Re-connect flows simple later. */
  const loadAll = useCallback(async () => {
    try {
      const [cfgResp, connResp] = await Promise.all([
        axiosInstance.get('/whatsapp/v2/meta/embedded-signup/config').catch(() => null),
        axiosInstance.get('/whatsapp/v2/meta/embedded-signup/connection').catch(() => null),
      ]);
      if (cfgResp?.data?.ok) setSignupUrl(cfgResp.data.signupUrl);
      if (connResp?.data?.ok && connResp.data.connected) {
        setConnection(connResp.data.connection);
        setStatus(STATUS.CONNECTED);
      } else {
        setStatus(STATUS.NOT_CONNECTED);
      }
    } catch (err) {
      console.warn('[WhatsAppSetup] loadAll failed:', err?.message);
      setStatus(STATUS.NOT_CONNECTED);
    }
  }, []);

  const exchangeCode = useCallback(async (code, metaParams = {}) => {
    setStatus(STATUS.CONNECTING);
    setError(null);
    try {
      const { data } = await axiosInstance.post(
        '/whatsapp/v2/meta/embedded-signup/exchange',
        { code, ...metaParams }
      );
      if (data?.ok) {
        setConnection(data.connection);
        setStatus(STATUS.CONNECTED);
        // Clean query params so a refresh doesn't re-exchange.
        navigate(location.pathname, { replace: true });
      } else {
        throw new Error(data?.error?.message || 'Exchange failed');
      }
    } catch (err) {
      const apiError = err.response?.data;
      const msg =
        apiError?.error?.message ||
        (typeof apiError?.error === 'string' ? apiError.error : null) ||
        err.message ||
        'Unable to complete the connection.';
      setError(`${msg}${apiError?.stage ? ` (stage: ${apiError.stage})` : ''}`);
      setStatus(STATUS.ERROR);
    }
  }, [location.pathname, navigate]);

  /* On mount: if Meta redirected us back with ?code=, exchange.
     Otherwise check connection state. */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    if (code) {
      exchangeCode(code, Object.fromEntries(params.entries()));
    } else {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Meta-hosted Embedded Signup posts `WA_EMBEDDED_SIGNUP` events
     to the opener window during the signup flow. The FINISH event
     carries `data.catalog_id`, `data.waba_id`, `data.phone_number_id`
     — the merchant's explicit selections. We stash these so we can
     forward them to /exchange alongside the OAuth code (which comes
     back via popup redirect a moment later). */
  const sessionInfoRef = useRef({});
  useEffect(() => {
    function onMessage(event) {
      // Meta serialises the payload as a JSON string in some flows.
      let payload = event.data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch { return; }
      }
      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;
      // Accept both the FINISH (standard) and
      // FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING (Coexistence) events.
      if (!['FINISH', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING', 'FINISH_ONLY_WABA'].includes(payload.event)) return;
      const d = payload.data || {};
      sessionInfoRef.current = {
        waba_id: d.waba_id || d.wabaId,
        phone_number_id: d.phone_number_id || d.phoneNumberId,
        business_id: d.business_id || d.businessId,
        catalog_id: d.catalog_id || d.catalogId,
        event: payload.event,
      };
      console.log('[WhatsAppSetup] captured session_info:', sessionInfoRef.current);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const handleConnect = useCallback(() => {
    if (!signupUrl) return;
    // Open the Meta-hosted signup in a popup so its window.opener is
    // us, which lets Meta postMessage session_info events back. If
    // popups are blocked, fall back to same-tab navigation.
    const popup = window.open(
      signupUrl,
      'wa_embedded_signup',
      'width=600,height=720,menubar=no,toolbar=no,resizable=yes,scrollbars=yes',
    );
    if (!popup) {
      window.location.href = signupUrl;
      return;
    }
    // Poll for the popup closing OR the popup navigating back to our
    // callback URL with ?code=…. The redirect happens in the popup;
    // read the code from popup.location.search once the URL is on our
    // origin (throws until same-origin, hence the try/catch).
    const poll = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(poll);
        return;
      }
      try {
        const url = new URL(popup.location.href);
        const code = url.searchParams.get('code');
        if (code) {
          window.clearInterval(poll);
          popup.close();
          exchangeCode(code, sessionInfoRef.current);
        }
      } catch {
        // Cross-origin — still on Meta's side, keep polling.
      }
    }, 500);
  }, [signupUrl, exchangeCode]);

  const handleDisconnect = useCallback(async () => {
    const ok = await confirm(
      'Conversations and messages will be removed. You can reconnect anytime.',
      { title: 'Disconnect WhatsApp', confirmText: 'Disconnect', type: 'danger' }
    );
    if (!ok) return;
    setDisconnecting(true);
    try {
      await axiosInstance.post('/whatsapp/v2/meta/embedded-signup/disconnect');
      setConnection(null);
      setStatus(STATUS.NOT_CONNECTED);
      toast.success('WhatsApp disconnected');
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.response?.data?.error || err.message;
      toast.error(`Disconnect failed: ${msg}`);
    } finally {
      setDisconnecting(false);
    }
  }, [confirm, toast]);

  return (
    <Shell>
      <FeatureGate
        featureId="marketing.whatsapp"
        upgradeMessage="WhatsApp is part of the Marketing tier. Upgrade to PRO or PREMIUM to connect your WABA."
      >
        <div className="wa-setup-page">
          <header className="wa-setup-header">
            <h1>WhatsApp setup</h1>
            <p>Connect your WhatsApp Business Account so customer messages land in your Revstar inbox.</p>
          </header>

          {status === STATUS.LOADING && <LoadingCard />}
          {status === STATUS.NOT_CONNECTED && (
            <ConnectCard signupUrl={signupUrl} onConnect={handleConnect} />
          )}
          {status === STATUS.CONNECTING && <ConnectingCard />}
          {status === STATUS.ERROR && <ConnectingCard error={error} />}
          {status === STATUS.CONNECTED && connection && (
            <ConnectedCard
              connection={connection}
              onDisconnect={handleDisconnect}
              disconnecting={disconnecting}
              toast={toast}
              onConnectionUpdated={loadAll}
            />
          )}
        </div>
      </FeatureGate>
    </Shell>
  );
}
